<template>
  <b-card>
    <loading
      v-if="isDuplicating"
      :active="true"
      :is-full-page="true"
      :color="colors.primary"
    />
    <b-row>
      <b-col v-if="!['supervisor', 'director'].includes(role) && isOnline" sm="4" class="pb-1">
        <b-button
          class="w-100"
          variant="outline-primary"
          :to="{ name: 'organization-process-add' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('message.Add_Process') }}
        </b-button>
      </b-col>

      <b-col v-if="!['supervisor', 'director'].includes(role)" sm="4"></b-col>

      <b-col v-if="isOnline" sm="4">
        <b-form-group>
          <b-form-input
            v-model="searchQuery"
            type="search"
            class="d-inline-block"
            :placeholder="$t('message.search')"
          />
        </b-form-group>
      </b-col>

      <b-col v-if="!['supervisor', 'director'].includes(role) && isOnline">
        <filters
          :locationFilterShow="true"
          :physicalLocationFilterShow="true"
          @changeFilter="updateFilter"
        />
      </b-col>

      <b-col cols="12">
        <b-table
          ref="refProcessListTable"
          :items="processes"
          hover
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty=""
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
        >
          <!-- <template #cell(locationsWithPhysicalLocations)="data">
            {{ data.item.locationsWithPhysicalLocations.join(', ') }}
          </template> -->

          <template #cell(activities)="data">
            <ol>
              <li
                v-for="(activity, index) in data.item.activities"
                :key="`${activity.name}-${index}`"
              >
                {{ activity.name }}
              </li>
            </ol>
          </template>

          <template #cell(requireImage)="data">
            <span v-if="data.item.requireImage">{{ $t('message.yes') }}</span>
            <span v-else>{{ $t('message.no') }}</span>
          </template>

          <template #cell(requireTimeCapture)="data">
            <span v-if="data.item.requireTimeCapture">{{ $t('message.yes') }}</span>
            <span v-else>{{ $t('message.no') }}</span>
          </template>

          <template #cell(actions)="data">
            <div class="flex flex-column">
              <NavigationButton @clicked="router.push({ name: 'habit-confirmation-new', params: { id: data.item._id } })" />
              <span v-if="!['supervisor', 'director'].includes(role) && isOnline">
                <EditButton @clicked="router.push({ name: 'organization-process-edit', params: { id: data.item._id } })" />
                <DuplicateButton @clicked="duplicateProcess(data.item._id)" />
                <DeleteButton v-b-modal="`modal ${data.item._id}`"/>
                <b-modal
                  :id="`modal ${data.item._id}`"
                  centered
                  no-close-on-backdrop
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.delete')"
                  :cancel-title="$t('message.cancel')"
                  @ok="deleteProcess(data.item._id)"
                >
                  {{ $t('message.confirm_delete_process') }}
                </b-modal>
              </span>
            </div>
          </template>
        </b-table>
      </b-col>

      <b-col
        v-if="isOnline"
        cols="12"
        class="d-flex justify-content-between flex-wrap"
      >
        <div class="mb-1">
          <b-form-group class="mb-0" >
            <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="perPageOptions"
              class="w-50"
            />
          </b-form-group>
        </div>

        <div class="mb-1">
          <span class="text-muted">{{ showingMessage }}</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalProcesses"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { BPagination, BTable, BModal, VBModal } from 'bootstrap-vue'
import { onMounted, computed } from '@vue/composition-api'
import store from '@/store'
import { useRouter } from '@core/utils/utils'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import DuplicateButton from '@/views/components/Shared/Buttons/DuplicateButton.vue'
import NavigationButton from '@/views/components/Shared/Buttons/NavigationButton.vue'
import useProcessList from './useProcessList'
import Filters from "@/views/organization/Filters.vue";
import { colors } from '@/constants'

export default {
  components: {
    BTable,
    BPagination,
    EditButton,
    DeleteButton,
    DuplicateButton,
    NavigationButton,
    BModal,
    Filters,
    Loading,
  },
  directives: {
    'b-modal': VBModal,
  },
  setup() {
    const { role } = store.state?.userStore?.userData
    const { router } = useRouter()
    const isOnline = computed(() => store.state.app.isOnline)

    const {
      fetchProcesses,
      tableColumns,
      perPage,
      currentPage,
      perPageOptions,
      totalProcesses,
      showingMessage,
      searchQuery,
      sortBy,
      isSortDirDesc,
      processes,
      deleteProcess,
      duplicateProcess,
      isDuplicating,
      isLoading,
      updateFilter,
    } = useProcessList()

    onMounted(() => {
      fetchProcesses()
    })
    
    return {
      fetchProcesses,
      processes,
      showingMessage,
      router,
      tableColumns,
      searchQuery,
      sortBy,
      isSortDirDesc,
      role,
      deleteProcess,
      duplicateProcess,
      isDuplicating,
      totalProcesses,
      currentPage,
      perPageOptions,
      perPage,
      isOnline,
      isLoading,
      updateFilter,
      colors,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.process-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
